const ROLE_SIZES = {
    boolean: { min: 56, preferred: 72, max: 88 },
    quantity: { min: 64, preferred: 76, max: 100 },
    // Đơn vị tính: hẹp, luôn đi kèm cột số lượng.
    unit: { min: 48, preferred: 64, max: 100 },
    money: { min: 96, preferred: 124, max: 160 },
    date: { min: 104, preferred: 130, max: 160 },
    status: { min: 88, preferred: 112, max: 144 },
    identity: { min: 96, preferred: 112, max: 160 },
    sku: { min: 160, preferred: 180, max: 260 },
    lookup: { min: 88, preferred: 96, max: 140 },
    primaryText: { min: 150, preferred: 220, max: 340 },
    name: { min: 130, preferred: 190, max: 300 },
    longText: { min: 180, preferred: 260, max: 380 },
    other: { min: 80, preferred: 120, max: 180 }
};

const FINANCIAL_TABLE_PATTERN = /(^|_)(GL|PNL|VAT|TAX|EXR)|Report|Statement|Ledger|Posting|Journal|TrialBalance|BalanceSheet|IncomeStatement/i;

const appendCssClass = (value, className) => {
    const classes = String(value || '')
        .split(/\s+/)
        .filter(Boolean);
    if (!classes.includes(className)) classes.push(className);
    return classes.join(' ');
};

export const getLookupBestFitWidth = (measuredWidth) => Math.ceil(Number(measuredWidth) || 0) + 12;

export const localizeLookupValues = (values, displayField, localize) => {
    if (!(values instanceof Array)) return values;

    return values.map((value) => {
        if (!value || typeof value !== 'object') return value;

        const localizedValue = { ...value };
        if (localizedValue[displayField] !== undefined && localizedValue[displayField] !== null)
            localizedValue[displayField] = localize(localizedValue[displayField]);
        if (displayField !== 'name' && localizedValue.name !== undefined && localizedValue.name !== null)
            localizedValue.name = localize(localizedValue.name);
        return localizedValue;
    });
};

const estimateHeaderMinWidth = (column, sizes) => {
    const caption = String(column.caption || column.label || '').trim();
    if (!caption) return sizes.min;

    const words = caption.split(/\s+/);
    const longestWordLength = words.reduce((maxLength, word) => Math.max(maxLength, Array.from(word).length), 0);
    const singleLineWidth = Math.ceil(Array.from(caption).length * 7.2 + 40);
    if (words.length <= 3 && singleLineWidth <= 240) return Math.max(sizes.min, singleLineWidth);

    const widthForLongestWord = Math.ceil(longestWordLength * 7.2 + 32);
    return Math.max(sizes.min, Math.min(widthForLongestWord, sizes.preferred));
};

const isMoneyField = (field, colType) =>
    colType === 'currency' || /(^|_)(amt|amount|price|cost|balance|credit|debit|gross|net|tax|revenue|expense|profit|total)(_|$)/i.test(field);

const isQuantityField = (field, colType) =>
    ['int', 'float', 'decimal', 'fixedPoint', 'percent'].includes(colType) && /(^|_)(qty|quantity|count|pct|percent|rate)(_|$)/i.test(field);

export const inferGridColumnRole = (column) => {
    const field = String(column.dataField || '');
    const colType = String(column.colType || column.dataType || '');

    if (column.isSystemCol) return 'system';
    if (/(^|_)uom(_(code|name))?$/i.test(field)) return 'unit';
    if (/(^|_)(sku|barcode)(_|$)|(^|_)short_code$/i.test(field)) return 'sku';
    if (/(^code$|_code$|barcode|short_code)/i.test(field)) return 'identity';
    if (column.lookup) return 'lookup';
    if (colType === 'bool' || colType === 'boolean' || /^is_/i.test(field)) return 'boolean';
    if (/(^|_)(status|state)(_|$)/i.test(field)) return 'status';
    if (['date', 'datetime', 'time', 'weekday', 'month', 'year'].includes(colType) || /(^|_)(date|month|year)(_|$)|_at$/i.test(field)) return 'date';
    if (isQuantityField(field, colType) || /(^|_)(qty|quantity|count|pct|percent)(_|$)/i.test(field)) return 'quantity';
    if (isMoneyField(field, colType)) return 'money';
    if (['int', 'float', 'decimal', 'fixedPoint', 'percent', 'number'].includes(colType)) return 'quantity';
    if (['', 'string', 'lookup', 'join', 'first', 'last'].includes(colType) && /(^|_)(department|business_unit|job_title|position|item_group|item_category|category|industry|brand)(_(name|title))?$/i.test(field)) return 'lookup';
    if (/(^|_)(description|desc|note|content|memo|comment|error|info|reason|tags?)(_|$)/i.test(field)) return 'longText';
    if (/(^|_)(name|title|label|address|dimens)(_|$)/i.test(field)) return 'name';
    return 'other';
};

const resolveGridColumnRole = (column, rawColumn, lookupFields) => {
    let role = inferGridColumnRole(column);
    const hasLookup = Boolean(rawColumn.lookup || column.lookup || (column.config && column.config.lookup) || lookupFields.has(column.dataField));
    if (hasLookup && role !== 'identity' && role !== 'sku' && role !== 'unit') role = 'lookup';
    return role;
};

const getPrimaryScore = (column) => {
    const field = String(column.dataField || '');
    let score = 0;

    if (/(^|_)item_name$/i.test(field)) score = 100;
    else if (/(^|_)entity_(full_)?name$/i.test(field)) score = 92;
    else if (/^name$/i.test(field)) score = 88;
    else if (/(^|_)(product|service|asset|employee|customer|vendor)_name$/i.test(field)) score = 84;
    else if (/_name$/i.test(field)) score = 64;
    else if (/^code$|_code$/i.test(field)) score = 52;
    else if (/(description|_desc)$/i.test(field)) score = 44;

    if (column.link) score += 8;
    if (column.visible === true) score += 4;
    if (column.defaultSort || column.sortOrder) score += 3;
    return score;
};

const getBaseImportance = (role) => {
    if (role === 'primaryText') return 92;
    if (role === 'quantity') return 80;
    // ĐVT đi cùng số lượng: ẩn cột ĐVT mà giữ số lượng thì số lượng mất nghĩa.
    if (role === 'unit') return 79;
    if (role === 'money') return 78;
    if (role === 'status') return 74;
    if (role === 'identity') return 70;
    if (role === 'sku') return 48;
    if (role === 'lookup') return 54;
    if (role === 'name') return 58;
    if (role === 'date') return 50;
    if (role === 'other') return 36;
    if (role === 'longText') return 28;
    if (role === 'boolean') return 24;
    return 0;
};

export const detectFinancialGrid = (columns, options = {}) => {
    if (options.mode === 'financial' || options.mode === 'report') return true;
    if (options.mode === 'standard') return false;

    const dataColumns = columns.filter((column) => !column.isSystemCol && column.visible !== false);
    const financialColumns = dataColumns.filter((column) => inferGridColumnRole(column) === 'money');
    const tableName = String(options.tableName || '');

    if (FINANCIAL_TABLE_PATTERN.test(tableName)) return true;
    if (options.isItemGrid || options.allowEdit) return false;
    return financialColumns.length >= 4 && financialColumns.length / Math.max(dataColumns.length, 1) >= 0.35;
};

const normalizeWidth = (column, rawColumn, role, isFlexible, lookupBestFitWidth) => {
    const sizes = ROLE_SIZES[role] || ROLE_SIZES.other;
    const hasExplicitWidth = rawColumn && rawColumn.width !== undefined;
    const hasExplicitMinWidth = rawColumn && rawColumn.minWidth !== undefined;
    const configuredWidth = hasExplicitWidth ? rawColumn.width : column.width;
    const headerMinWidth = estimateHeaderMinWidth(column, sizes);
    const measuredMinWidth = typeof column.minWidth === 'number' ? column.minWidth : 0;
    const useBoundedBestFit = role === 'identity' || role === 'sku' || role === 'lookup' || role === 'unit';
    const hasLookupBestFitWidth = role === 'lookup' && typeof lookupBestFitWidth === 'number';
    const bestFitWidth = hasLookupBestFitWidth ? Math.max(headerMinWidth, lookupBestFitWidth) : null;

    if (typeof configuredWidth === 'number' || (typeof configuredWidth === 'string' && configuredWidth !== 'auto')) column.width = configuredWidth;
    else if (isFlexible) column.width = undefined;
    else if (hasLookupBestFitWidth) column.width = bestFitWidth;
    else if (useBoundedBestFit) column.width = Math.max(headerMinWidth, Math.min(Math.max(measuredMinWidth, sizes.min), sizes.max));
    else column.width = Math.max(sizes.preferred, headerMinWidth);

    if (hasExplicitMinWidth) column.minWidth = rawColumn.minWidth;
    else {
        column.minWidth = hasLookupBestFitWidth ? bestFitWidth : Math.max(headerMinWidth, Math.min(measuredMinWidth, sizes.max));
        if (typeof column.width === 'number' && column.minWidth > column.width) column.minWidth = column.width;
    }
};

export const buildSmartGridLayout = (columns, options = {}) => {
    const rawColumns = options.rawColumns instanceof Array ? options.rawColumns : [];
    const lookupFields =
        options.lookupFields instanceof Set ? options.lookupFields : new Set(options.lookupFields instanceof Array ? options.lookupFields : []);
    const lookupBestFitWidths =
        options.lookupBestFitWidths instanceof Map
            ? options.lookupBestFitWidths
            : new Map(
                  options.lookupBestFitWidths && typeof options.lookupBestFitWidths === 'object' ? Object.entries(options.lookupBestFitWidths) : []
              );
    const rawByField = new Map(
        rawColumns
            .filter((column) => column && typeof column === 'object')
            .map((column) => [column.dataField, column.rawConfig ? { ...column.rawConfig, dataField: column.dataField } : column])
    );
    const dataColumns = columns.filter((column) => !column.isSystemCol);
    const visibleColumns = dataColumns.filter((column) => column.visible !== false);
    const primaryColumn = [...visibleColumns].sort((a, b) => getPrimaryScore(b) - getPrimaryScore(a))[0];
    const canAutoSize = (column) => {
        const rawColumn = rawByField.get(column.dataField) || {};
        const width = rawColumn.width ?? column.width;
        return width === undefined || width === 'auto';
    };
    const requestsFlexibleWidth = (column) => (rawByField.get(column.dataField)?.width ?? column.width) === 'auto';
    const autoCandidates = visibleColumns.filter(canAutoSize);
    const textCandidates = autoCandidates.filter((column) =>
        ['name', 'longText', 'other'].includes(resolveGridColumnRole(column, rawByField.get(column.dataField) || {}, lookupFields)));
    const explicitFlexible = textCandidates.filter(requestsFlexibleWidth);
    // Prefer free text as the fallback receiver. A lookup/code can stretch only
    // when there is no other automatic text column to take the remaining space.
    const fallbackFlexible = [...(textCandidates.length ? textCandidates : autoCandidates)].sort((a, b) => {
        const score = (column) => {
            const role = resolveGridColumnRole(column, rawByField.get(column.dataField) || {}, lookupFields);
            return (['name', 'longText', 'other'].includes(role) ? 1000 : 0) + getPrimaryScore(column);
        };
        return score(b) - score(a);
    })[0];
    const isItemGrid = Boolean(options.isItemGrid || options.allowEdit);
    const financialMode = detectFinancialGrid(columns, { ...options, isItemGrid });
    const layoutMeta = new Map();

    dataColumns.forEach((column, index) => {
        const rawColumn = rawByField.get(column.dataField) || {};
        let role = resolveGridColumnRole(column, rawColumn, lookupFields);
        const isPrimary = column === primaryColumn && getPrimaryScore(column) > 0;
        if (isPrimary && (role === 'name' || role === 'longText' || role === 'other')) role = 'primaryText';

        const isFlexible = explicitFlexible.length ? explicitFlexible.includes(column) : column === fallbackFlexible;
        const configuredWidth = rawColumn.width !== undefined ? rawColumn.width : column.width;
        delete column._antBestFit;
        delete column._antFlexibleWidth;
        normalizeWidth(column, rawColumn, role, isFlexible, lookupBestFitWidths.get(column.dataField));

        // A fluid column must not make adaptivity reserve the full unwrapped
        // text width. Its pixel width is allocated from the container instead.
        if (options.columnAutoWidth !== true && isFlexible && (configuredWidth === undefined || configuredWidth === 'auto')) {
            column._antFlexibleWidth = true;
            column.width = column.minWidth;
        }

        // Retain the default layout so a different page can undo a previous
        // content fit. Explicit widths, editors and custom templates own their size.
        if (
            options.columnAutoWidth !== true &&
            (configuredWidth === undefined || configuredWidth === 'auto') &&
            !column.allowEditing && !rawColumn.editable &&
            [undefined, 'renderLinkCell', 'renderBoldCell', 'renderCheckboxCell'].includes(column.cellTemplate)
        ) {
            const lookup = rawColumn.lookup || column.lookup || column.config?.lookup;
            if (!column._antLookupTexts && Array.isArray(lookup?.values)) {
                const displayField = column.displayField || lookup.displayField || lookup.displayExpr || 'name';
                column._antLookupTexts = lookup.values.map((value) => typeof value === 'object' ? value?.[displayField] : value);
            }
            column._antBestFit = {
                version: 2,
                role,
                minimum: Number(rawColumn.minWidth ?? column.config?.minWidth) || 0,
                width: column.width,
                minWidth: column.minWidth
            };
        }

        const isEditable = Boolean(column.allowEditing || rawColumn.editable);
        if (isEditable && rawColumn.allowHiding === undefined) column.allowHiding = false;

        let importance = getBaseImportance(role);
        if (isFlexible) importance += 12;
        if (rawColumn.visible === true) importance += 12;
        if (column.link || column.defaultSort || column.sortOrder) importance += 8;
        if (isEditable) importance += 300;
        if (rawColumn.allowHiding === false || column.allowHiding === false) importance += 1000;
        if (rawColumn.hidingPriority !== undefined && Number.isFinite(Number(rawColumn.hidingPriority)))
            importance += 200 + Number(rawColumn.hidingPriority);

        const hasExternalCustomTemplate = column.cellTemplate === 'renderCustomTemplate';
        if (!hasExternalCustomTemplate && !column.allowEditing) {
            column.cssClass = appendCssClass(column.cssClass, 'ant-grid-cell-smart');
            if (isItemGrid && isPrimary && column.cellTemplate === undefined) {
                column.cssClass = appendCssClass(column.cssClass, 'ant-grid-cell-wrap-2');
                column.cellTemplate = 'renderSmartTextCell';
            } else column.cssClass = appendCssClass(column.cssClass, 'ant-grid-cell-ellipsis');
        }
        layoutMeta.set(column, { importance, index });
    });

    const hideableColumns = dataColumns
        .filter((column) => column.allowHiding !== false)
        .sort((a, b) => layoutMeta.get(a).importance - layoutMeta.get(b).importance || layoutMeta.get(a).index - layoutMeta.get(b).index);

    hideableColumns.forEach((column, index) => {
        column.hidingPriority = index;
    });
    dataColumns
        .filter((column) => column.allowHiding === false)
        .forEach((column, index) => {
            column.hidingPriority = hideableColumns.length + index;
        });

    return {
        columns,
        financialMode,
        columnAutoWidth: options.columnAutoWidth !== undefined ? options.columnAutoWidth : false,
        columnHidingEnabled: options.columnHidingEnabled !== undefined ? options.columnHidingEnabled : !financialMode,
        showScrollbar: options.showScrollbar || (financialMode ? 'always' : 'onHover')
    };
};

export default {
    buildSmartGridLayout,
    detectFinancialGrid,
    inferGridColumnRole,
    getLookupBestFitWidth,
    localizeLookupValues
};

// Adaptive (columnHidingEnabled) chỉ ẩn cột có hidingPriority. Cột đang sắp xếp: tạm bỏ
// hidingPriority để không bao giờ bị ẩn; bỏ sắp xếp thì trả mức ưu tiên cũ.
const sortedPriorities = new WeakMap();
export const keepSortedColumnsShown = (instance, columns) => {
    if (!instance || !(columns instanceof Array)) return 0;
    let saved = sortedPriorities.get(instance);
    if (!saved) {
        saved = new Map();
        sortedPriorities.set(instance, saved);
    }
    let changes = 0;
    columns.forEach((column) => {
        const key = column && (column.name || column.dataField);
        if (!key || column.isSystemCol) return;
        const sorted = Boolean(instance.columnOption(key, 'sortOrder'));
        const priority = instance.columnOption(key, 'hidingPriority');
        if (sorted) {
            if (priority === undefined || priority === null) return;
            if (!saved.has(key)) saved.set(key, priority);
            instance.columnOption(key, 'hidingPriority', undefined);
            changes++;
        } else if (saved.has(key)) {
            instance.columnOption(key, 'hidingPriority', saved.get(key));
            saved.delete(key);
            changes++;
        }
    });
    return changes;
};
